// WealthMethodCheckout.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Shield,
  CheckCircle,
  User,
  Mail,
  Phone,
  Award,
  Lock,
} from "lucide-react";
import bookCover from "../../../assets/images/mockup1.png";
import { useLocation } from "react-router-dom";
import { useInitializeWealthMethodMutation } from "../../../app/api/wealthMethodApi";

interface FormData {
  firstName: string;
  email: string;
  phoneNo: string;
}

interface FormErrors {
  firstName?: string;
  email?: string;
  phoneNo?: string;
}

interface SelectedOption {
  id: string;
  name: string;
  price: string; // e.g. "₦45,000"
  original?: string;
  tag?: string;
  features?: Array<string | { title: string; value?: string | number }>;
  popular?: boolean;
}

// Local path to uploaded file (will be transformed by your toolchain)
const BOOK_FILE_URL = "/mnt/data/W.E.A.L.T.H METHOD.docx";

const WealthMethodCheckout: React.FC = () => {
  const location = useLocation();
  const passed =
    (location.state && (location.state as any).selectedOption) || null;
  const [selectedOption, setSelectedOption] = useState<SelectedOption | null>(
    passed,
  );

  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    email: "",
    phoneNo: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  console.log(loading);

  // RTK Query mutation for wealth method initialize
  const [initializeWealthMethod] = useInitializeWealthMethodMutation();

  useEffect(() => {
    if (passed) setSelectedOption(passed as SelectedOption);
  }, [passed]);

  // --- Helpers: parse price and format currency
  const parsePriceString = (price?: string): number => {
    if (!price) return 0;
    const digits = price.replace(/[^\d]/g, "");
    return digits ? Number(digits) : 0;
  };

  const formatCurrency = (n: number) => "₦" + n.toLocaleString("en-NG" as any);

  // --- Base heuristic estimates (reasonable, non-arbitrary)
  // These base values represent sensible retail estimates for each feature type.
  const baseEstimate = (title: string): number => {
    const t = title.toLowerCase();
    if (t.includes("90-day") || t.includes("quick start")) return 50000; // ₦50k base
    if (t.includes("chapter") && t.includes("video")) return 30000; // ₦30k
    if (t.includes("video")) return 25000; // ₦25k
    if (t.includes("mastermind")) return 80000; // ₦80k
    if (t.includes("toolkit") || t.includes("templates") || t.includes("tool"))
      return 30000; // ₦30k
    if (t.includes("coaching") || t.includes("calls")) return 100000; // ₦100k
    if (t.includes("inner circle") || t.includes("inner")) return 60000; // ₦60k
    if (t.includes("shipping") || t.includes("signature")) return 5000; // ₦5k
    if (t.includes("ebook") || t.includes("digital")) return 35000; // ₦35k
    // default sensible base
    return 20000; // ₦20k
  };

  // Benefit-first copy generator (communicative)
  const describeFeature = (title: string): string => {
    const t = title.toLowerCase();
    if (t.includes("90-day") || t.includes("quick start"))
      return "90-day implementation roadmap with weekly actions — reduces guesswork and drives consistent progress.";
    if (t.includes("video") && t.includes("chapter"))
      return "Chapter-by-chapter video walkthroughs that make implementation fast and frictionless.";
    if (t.includes("video"))
      return "Actionable video lessons to shorten the learning curve and speed results.";
    if (t.includes("mastermind"))
      return "Access to a focused peer mastermind for accountability, feedback and growth-focused networking.";
    if (t.includes("toolkit") || t.includes("templates"))
      return "Plug-and-play templates and funnels so you launch and convert faster.";
    if (t.includes("coaching") || t.includes("calls"))
      return "Live coaching to remove blockers and accelerate traction.";
    if (t.includes("inner circle") || t.includes("inner"))
      return "VIP community with ongoing mentorship and strategy sessions.";
    if (t.includes("shipping") || t.includes("signature"))
      return "Premium physical extras (signed copy, priority handling) for a premium experience.";
    if (t.includes("ebook") || t.includes("digital"))
      return "Instant digital access — start reading and executing immediately.";
    return "A focused resource designed to speed implementation and deliver measurable results.";
  };

  // --- Build structured features from selectedOption.features
  const featuresList = useMemo(() => {
    const raw = selectedOption?.features || [];
    return raw.map((f) => {
      if (typeof f === "string") {
        const title = f;
        const value = baseEstimate(title);
        return { title, description: describeFeature(title), value };
      } else {
        const title = (f as any).title || "Feature";
        const rawValue = (f as any).value;
        let valueNum = 0;
        if (typeof rawValue === "string" && rawValue.trim().length) {
          const digits = rawValue.replace(/[^\d]/g, "");
          valueNum = digits ? Number(digits) : baseEstimate(title);
        } else if (typeof rawValue === "number") {
          valueNum = rawValue;
        } else {
          valueNum = baseEstimate(title);
        }
        return { title, description: describeFeature(title), value: valueNum };
      }
    });
  }, [selectedOption]);

  // --- Determine a sensible target total based on product price
  // Use a multiplier so value stacking scales with price. Clamp to not exceed top-caps.
  const computeTargetTotal = (priceNumeric: number) => {
    if (priceNumeric <= 0) return 0;
    // multiplier = 4x retail value by default
    const multiplier = 4;
    const rawTarget = priceNumeric * multiplier;
    // clamp: min 20000, max 185000 (per your earlier cap)
    const MIN = 20000;
    const MAX = 185000;
    return Math.max(MIN, Math.min(MAX, rawTarget));
  };

  // --- Scale feature values proportionally if sum < targetTotal
  const adjustedFeatures = useMemo(() => {
    const baseList = featuresList.slice();
    const currentTotal = baseList.reduce((s, f) => s + (f.value || 0), 0);
    const priceNumeric = parsePriceString(selectedOption?.price || "₦0");
    const targetTotal = computeTargetTotal(priceNumeric);

    if (currentTotal === 0) return baseList;

    // If current total already >= target, keep base estimates (no arbitrary inflation)
    if (currentTotal >= targetTotal) return baseList;

    // Else scale each feature proportionally so newTotal == targetTotal
    const scale = targetTotal / currentTotal;
    return baseList.map((f) => ({
      ...f,
      value: Math.round((f.value || 0) * scale),
    }));
  }, [featuresList, selectedOption]);

  const totalFeaturesValue = useMemo(
    () => adjustedFeatures.reduce((s, f) => s + (f.value || 0), 0),
    [adjustedFeatures],
  );

  // --- Form validation and submission (keeps prior API contract)
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "Full name is required";
    if (!formData.email.trim()) newErrors.email = "Email address is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Please enter a valid email address";
    if (!formData.phoneNo.trim())
      newErrors.phoneNo = "Phone number is required";
    else if (!/^[\d\s+\-()]+$/.test(formData.phoneNo))
      newErrors.phoneNo = "Please enter a valid phone number";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors])
      setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    setLoading(true);
    setMessage("");

    try {
      // Build payload matching server expectation
      const productPayload = selectedOption
        ? {
            id: selectedOption.id,
            name: selectedOption.name,
            price: selectedOption.price,
            priceNumeric: parsePriceString(selectedOption.price),
          }
        : {
            id: "default",
            name: "The WEALTH Method Complete Manual",
            price: "₦5,000",
            priceNumeric: 5000,
          };

      const featuresPayload = adjustedFeatures.map((f) => ({
        title: f.title,
        description: f.description,
        value: f.value,
      }));

      const totalsPayload = {
        featuresTotal: totalFeaturesValue,
        productPrice: productPayload.priceNumeric,
      };

      // Use RTK mutation to initialize payment
      const res = await initializeWealthMethod({
        firstName: formData.firstName,
        email: formData.email,
        phoneNo: formData.phoneNo,
        product: {
          ...productPayload,
          sourceFileUrl: BOOK_FILE_URL, // include local path per your tooling note
        },
        features: featuresPayload,
        totals: totalsPayload,
        metadata: {
          packageName: productPayload.name,
          packageId: productPayload.id,
        },
      }).unwrap();

      // server returns authorization_url (or authorizationUrl depending)
      const authUrl =
        (res as any).authorization_url ||
        (res as any).authorizationUrl ||
        (res as any).authorizationURL;

      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setMessage(
          (res as any).message ||
            "Failed to initialize payment. Please try again.",
        );
      }
    } catch (err: any) {
      setMessage(
        err?.data?.message ||
          err?.message ||
          "An error occurred. Please try again later.",
      );
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const getPriceLabel = () =>
    (selectedOption && selectedOption.price) || "₦5,000";

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Complete Your Order
          </h1>
          <p className="text-lg text-gray-600">
            You're one step away — here's exactly what you're getting and why
            it's worth many times your investment.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-lg p-6 h-fit">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Your Order
            </h2>

            <div>
              <div className="rounded-lg p-6 border border-yellow-200">
                <div className="bg-gray-200 w-full rounded mb-4 flex items-center justify-center">
                  <img src={bookCover} alt="" />
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    {selectedOption?.name ||
                      "The WEALTH Method Complete Manual"}
                  </h3>
                  <p className="text-sm text-gray-600">
                    156 pages • Practical frameworks • Local examples
                  </p>
                  <p className="text-lg font-bold text-green-600 mt-2">
                    {getPriceLabel()}
                  </p>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-4 mb-6">
              <h4 className="font-semibold text-gray-800">Included Features</h4>
              <p className="text-sm text-gray-600">
                Each feature below is selected to accelerate implementation and
                drive measurable results — we've stacked high-value resources so
                you get outsized ROI.
              </p>

              <div className="mt-3 space-y-3">
                {adjustedFeatures.map((f, idx) => (
                  <div
                    key={idx}
                    className="p-3 rounded-lg border border-gray-100 bg-gray-50"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">
                          {f.title}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {f.description}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">
                          Estimated value
                        </div>
                        <div className="font-bold text-green-600">
                          {formatCurrency(f.value || 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Breakdown & Totals */}
            <div className="bg-white/90 rounded-lg p-4 border border-gray-100 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm opacity-75">Total retail value:</span>
                <span className="text-lg">
                  {formatCurrency(totalFeaturesValue)}
                </span>
              </div>
              <div className="flex justify-between items-center text-yellow-300">
                <span className="text-lg font-semibold">Your investment:</span>
                <span className="text-2xl font-bold">{getPriceLabel()}</span>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                This offer stacks proven tools, mentorship and implementation
                assets — designed to accelerate your business to the next level.
              </div>
            </div>

            {/* Guarantee */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
              <div className="flex items-center mb-2">
                <Shield className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-semibold text-blue-800">
                  90-Day Money-Back Guarantee
                </span>
              </div>
              <p className="text-sm text-blue-700">
                Implement the system for 90 days — if you're not satisfied we'll
                refund you.
              </p>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Your Details
            </h2>

            <div className="space-y-6">
              {/* Name Field */}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  First Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors ${
                      errors.firstName ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="Enter your first name"
                  />
                </div>
                {errors.firstName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="your.email@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label
                  htmlFor="phoneNo"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  WhatsApp Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    id="phoneNo"
                    name="phoneNo"
                    value={formData.phoneNo}
                    onChange={handleInputChange}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-colors ${
                      errors.phoneNo ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="+234 XXX XXX XXXX"
                  />
                </div>
                {errors.phoneNo && (
                  <p className="text-red-500 text-sm mt-1">{errors.phoneNo}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  For access to exclusive mastermind community
                </p>
              </div>

              {/* Submit Button */}
              <div
                onClick={handleSubmit}
                className={`w-full py-4 px-6 rounded-lg text-lg font-bold text-white transition-all duration-300 flex items-center justify-center cursor-pointer ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 transform hover:scale-105 shadow-lg"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Secure Checkout - {getPriceLabel()}
                  </>
                )}
              </div>

              {/* Message */}
              {message && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700">{message}</p>
                </div>
              )}

              {/* Security Notice */}
              <div className="flex items-center justify-center text-sm text-gray-500">
                <Lock className="w-4 h-4 mr-2" />
                <span>Your information is secure and encrypted</span>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
              <span>Instant Download</span>
            </div>
            <div className="flex items-center">
              <Shield className="w-4 h-4 text-blue-500 mr-1" />
              <span>90-Day Guarantee</span>
            </div>
            <div className="flex items-center">
              <Award className="w-4 h-4 text-purple-500 mr-1" />
              <span>200+ Success Stories</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WealthMethodCheckout;
